import React, { Suspense, Fragment, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import Loader from './components/Loader/Loader';
import AdminLayout from './layouts/AdminLayout';
import LandingLayout from './layouts/LandingLayout';

import GuestGuard from './components/Auth/GuestGuard';
import AuthGuard from './components/Auth/AuthGuard';
import GoldGuard from './components/Auth/GoldGuard';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';

import { BASE_URL } from './config/constant';

export const renderRoutes = (routes = []) => (
  <Suspense fallback={<Loader />}>
    <Routes>
      {routes.map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Element = route.element;

        return (
          <Route
            key={i}
            path={route.path}
            element={
              <ErrorBoundary>
                <Guard>
                  <Layout>{route.routes ? renderRoutes(route.routes) : <Element props={true} />}</Layout>
                </Guard>
              </ErrorBoundary>
            }
          />
        );
      })}
    </Routes>
  </Suspense>
);

const routes = [
  {
    exact: 'true',
    path: '/',
    layout: LandingLayout,
    element: lazy(() => import('./views/landing/LandingPage'))
  },
  {
    exact: 'true',
    path: '/platform',
    layout: LandingLayout,
    element: lazy(() => import('./views/landing/PlatformPage'))
  },
  {
    exact: 'true',
    path: '/solutions',
    layout: LandingLayout,
    element: lazy(() => import('./views/landing/SolutionsPage'))
  },
  {
    exact: 'true',
    path: '/about-contact',
    layout: LandingLayout,
    element: lazy(() => import('./views/landing/AboutContactPage'))
  },
  {
    exact: 'true',
    guard: GuestGuard,
    path: '/login',
    element: lazy(() => import('./views/auth/signin/SignIn1'))
  },
  {
    exact: 'true',
    path: '/public/audit-reports/:siteId/full-report',
    element: lazy(() => import('./views/audit-reports/FullReportView'))
  },
  {
    exact: 'true',
    path: '/terms-conditions',
    element: lazy(() => import('./views/legal/TermsConditions'))
  },
  {
    exact: 'true',
    path: '/privacy-policy',
    element: lazy(() => import('./views/legal/PrivacyPolicy'))
  },
  {
    exact: 'true',
    path: '/404',
    element: lazy(() => import('./views/errors/NotFound404'))
  },
  {
    exact: 'true',
    path: '/maintenance/coming-soon',
    element: lazy(() => import('./views/maintenance/ComingSoon'))
  },
  {
    exact: 'true',
    path: '/maintenance/error',
    element: lazy(() => import('./views/maintenance/Error'))
  },
  {
    exact: 'true',
    path: '/maintenance/offline-ui',
    element: lazy(() => import('./views/maintenance/OfflineUI'))
  },

  {
    exact: 'true',
    path: '/auth/signup-1',
    element: lazy(() => import('./views/auth/signup/SignUp1'))
  },
  {
    exact: 'true',
    path: '/auth/register',
    element: lazy(() => import('./views/auth/register/Register'))
  },
  {
    exact: 'true',
    path: '/auth/forgot-password',
    element: lazy(() => import('./views/auth/reset-password/ResetPassword1'))
  },
  {
    exact: 'true',
    path: '/auth/delegate-setup-password',
    element: lazy(() => import('./views/auth/signin/DelegatePasswordSetup'))
  },
  {
    exact: 'true',
    path: '/auth/reset-password',
    element: lazy(() => import('./views/auth/ChangePassword'))
  },
  {
    exact: 'true',
    path: '/auth/profile-settings',
    element: lazy(() => import('./views/auth/ProfileSettings'))
  },
  {
    exact: 'true',
    path: '/auth/otp-verification',
    element: lazy(() => import('./views/auth/reset-password/OtpVerification'))
  },
  {
    path: '*',
    layout: AdminLayout,
    guard: AuthGuard,
    routes: [
      // {
      //   exact: 'true',
      //   path: '/sample-page',
      //   element: lazy(() => import('./views/extra/SamplePage'))
      // },

      {
        exact: 'true',
        path: '/admin/audit-reports',
        element: lazy(() => import('./views/audit-reports/index'))
      },
      {
        exact: 'true',
        path: '/admin/audit-reports/:siteId',
        element: lazy(() => import('./views/audit-reports/ReportDetail'))
      },
      {
        exact: 'true',
        path: '/admin/audit-reports/:siteId/version/:version',
        element: lazy(() => import('./views/audit-reports/ReportVersionDetail'))
      },
      {
        exact: 'true',
        path: '/admin/audit-reports/:siteId/full-report',
        element: lazy(() => import('./views/audit-reports/FullReportView'))
      },
      {
        exact: 'true',
        path: '/admin/audit-reports/:siteId/enhanced-report',
        element: lazy(() => import('./views/audit-reports/EnhancedReportView'))
      },
      {
        exact: 'true',
        path: '/profile/edit-profile',
        element: lazy(() => import('./views/profile/editprofile'))
      },
      {
        exact: 'true',
        path: '/admin/help-tutorials',
        element: lazy(() => import('./views/help-tutorials/index'))
      },

      {
        exact: 'true',
        path: '/post/details',
        element: lazy(() => import('./views/post/ReportsData'))
      },
      {
        exact: 'true',
        path: '/post/data/details',
        element: lazy(() => import('./views/post/ReportsDataDetails'))
      },
      {
        exact: 'true',
        path: '/help-request/request-list',
        element: lazy(() => import('./views/help_request/HelpRequestListing'))
      },

      {
        exact: 'true',
        path: '/admin/dashboard',
        element: lazy(() => import('./views/home/index'))
      },
      {
        exact: 'true',
        path: '/admin/dashboard/success-analysis-details',
        element: lazy(() => import('./views/home/SuccessAnalysisDetails'))
      },
      {
        exact: 'true',
        path: '/admin/dashboard/full-report/:siteId',
        element: lazy(() => import('./views/home/SuccessFullReportView'))
      },
      {
        exact: 'true',
        path: '/admin/client-management',
        element: lazy(() => import('./views/client-management/index'))
      },
      {
        exact: 'true',
        path: '/admin/subscription',
        element: lazy(() => import('./views/subscription/index'))
      },
      {
        exact: 'true',
        path: '/admin/subscription/success',
        element: lazy(() => import('./views/subscription/Success'))
      },
      {
        exact: 'true',
        path: '/admin/subscription/cancel',
        element: lazy(() => import('./views/subscription/Cancel'))
      },
      {
        exact: 'true',
        path: '/admin/manage-auditors',
        element: lazy(() => import('./views/auditors/index'))
      },
      {
        exact: 'true',
        path: '/admin/manage-delegate-role',
        element: lazy(() => import('./views/delegate-role/index'))
      },
      {
        exact: 'true',
        path: '/admin/manage-templates',
        element: lazy(() => import('./views/questionnaire/ManageTemplate'))
      },
      {
        exact: 'true',
        path: '/admin/my-chapters',
        guard: GoldGuard,
        element: lazy(() => import('./views/my-chapters/index'))
      },
      {
        exact: 'true',
        path: '/admin/my-chapters/:chapterId',
        guard: GoldGuard,
        element: lazy(() => import('./views/my-chapters/ChapterDetails'))
      },
      {
        exact: 'true',
        path: '/admin/manage-templates/:templateId',
        element: lazy(() => import('./views/questionnaire/index'))
      },
      {
        exact: 'true',
        path: '/admin/manage-sites',
        element: lazy(() => import('./views/sites/index'))
      },
      {
        exact: 'true',
        path: '/admin/ibp-reference',
        element: lazy(() => import('./views/ibp-reference/index'))
      },
      {
        exact: 'true',
        path: '/admin/ai-assistant',
        element: lazy(() => import('./views/ai-assistant/index'))
      },
      {
        exact: 'true',
        path: '/admin/settings',
        element: lazy(() => import('./views/settings/index'))
      },
      {
        exact: 'true',
        path: '/admin/support-tickets',
        element: lazy(() => import('./views/support-tickets/index'))
      },
      {
        exact: 'true',
        path: '/admin/ai-documents',
        element: lazy(() => import('./views/ai-documents/index'))
      },
      {
        exact: 'true',
        path: '/admin/audit-log',
        element: lazy(() => import('./views/audit-log/index'))
      },
      {
        exact: 'true',
        path: '/admin/manage-sites/:id',
        element: lazy(() => import('./views/sites/SiteDetail'))
      },
      {
        exact: 'true',
        path: '/admin/site/:siteId/assessment/:chapterId',
        element: lazy(() => import('./views/sites/AssessmentView'))
      },
      {
        exact: 'true',
        path: '/admin/site/:siteId/assessment/:chapterId/report/:templateId',
        element: lazy(() => import('./views/sites/SummaryReportView'))
      },
      {
        path: '*',
        exact: 'true',
        element: () => <Navigate to={BASE_URL} />
      }
    ]
  }
];

export default routes;
